// A view's filter, sort and group over task cards (06 §Boards and tasks; M2-01 `ViewFilter`,
// `ViewSort`, `View.group_by`). The API evaluates saved views server-side (`boards/{id}/tasks
// ?view=`, M2-03); the in-memory twin runs this module as that server, and the board screen runs
// it over the board's tasks only while the person has unsaved changes to a view (06: per-person
// overrides do not save unless "Save view"). Pure: no clock unless the caller passes one.
import type { OperationOutput } from "@companyos/sdk";

export type TaskCard = OperationOutput<"tasks.list">["data"][number];
export type TaskDetail = OperationOutput<"tasks.get">;
export type Board = OperationOutput<"boards.get">;
export type FieldDef = OperationOutput<"fields.list">["data"][number];
export type View = OperationOutput<"views.list">["data"][number];
export type ViewFilter = View["filter"];
export type ViewSort = View["sort"];
export type ViewCondition = NonNullable<ViewFilter["all"]>[number];
export type StatusDef = Board["status_set"][number];

/** What a task needs to be filtered, sorted and grouped: the card fields the views read. */
export type CardLike = Pick<
  TaskCard,
  | "id"
  | "title"
  | "status"
  | "priority"
  | "number"
  | "due_at"
  | "start_at"
  | "created_at"
  | "completed_at"
  | "assignee_seat_ids"
  | "fields"
> &
  // WOS-BUNDLE: the `tag` filter reads the card's tags (optional: older callers' rows have none).
  Partial<Pick<TaskCard, "tag_ids">>;

export type ViewContext = {
  statuses: readonly StatusDef[];
  fields: readonly Pick<FieldDef, "id" | "name" | "type" | "options">[];
  /** A seat's label for assignee groups (title, or the DE holding it). */
  seatLabel?: (seatId: string) => string;
};

/** 0 none, 1 urgent … 4 low (the `task.priority` scale). */
export const PRIORITY_LABELS = ["No priority", "Urgent", "High", "Medium", "Low"] as const;

/** Group keys the views offer besides `field:{id}`. */
export const GROUP_KEYS = ["status", "priority", "assignee"] as const;

export function isDoneStatus(statuses: readonly StatusDef[], key: string): boolean {
  return statuses.find((s) => s.key === key)?.done ?? false;
}

function fieldValue(task: CardLike, field: string): unknown {
  switch (field) {
    case "title":
      return task.title;
    case "status":
      return task.status;
    case "priority":
      return task.priority;
    case "assignee":
      return task.assignee_seat_ids;
    case "due_at":
      return task.due_at;
    case "start_at":
      return task.start_at;
    case "created_at":
      return task.created_at;
    case "completed_at":
      return task.completed_at;
    case "number":
      return task.number;
    case "tag":
      return task.tag_ids ?? [];
    default:
      return field.startsWith("field:") ? task.fields[field.slice(6)] : undefined;
  }
}

const isEmpty = (v: unknown) =>
  v === null || v === undefined || v === "" || (Array.isArray(v) && v.length === 0);

function compare(a: unknown, b: unknown): number {
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b));
}

/** One condition. Array values (assignees, multi-selects) match when any element matches. */
export function test(task: CardLike, c: ViewCondition): boolean {
  const v = fieldValue(task, c.field);
  const list = Array.isArray(v) ? v : [v];
  const want = c.value;
  const wants = Array.isArray(want) ? want : [want];
  switch (c.op) {
    case "empty":
      return isEmpty(v);
    case "not_empty":
      return !isEmpty(v);
    case "eq":
      return list.some((x) => x === want);
    case "neq":
      return !list.some((x) => x === want);
    case "in":
      return list.some((x) => wants.includes(x));
    case "nin":
      return !list.some((x) => wants.includes(x));
    case "contains":
      return list.some((x) =>
        String(x ?? "")
          .toLowerCase()
          .includes(String(want ?? "").toLowerCase()),
      );
    case "lt":
      return !isEmpty(v) && compare(v, want) < 0;
    case "lte":
      return !isEmpty(v) && compare(v, want) <= 0;
    case "gt":
      return !isEmpty(v) && compare(v, want) > 0;
    case "gte":
      return !isEmpty(v) && compare(v, want) >= 0;
  }
}

/** Every `all` condition, at least one `any` condition (when there are any), and done tasks only
 * when `include_done` is not false. */
export function matches(task: CardLike, filter: ViewFilter, ctx: ViewContext): boolean {
  if (filter.include_done === false && isDoneStatus(ctx.statuses, task.status)) return false;
  if (!(filter.all ?? []).every((c) => test(task, c))) return false;
  const any = filter.any ?? [];
  return any.length === 0 || any.some((c) => test(task, c));
}

/** Sorted by the view's keys, then by number (creation order), so the order is total. Empty
 * values sort last in either direction; priority 0 (none) counts as empty. */
export function sortCards<T extends CardLike>(
  tasks: readonly T[],
  sort: ViewSort,
  ctx: ViewContext,
): T[] {
  const statusIndex = new Map(ctx.statuses.map((s, i) => [s.key, i]));
  const key = (t: T, field: string): unknown => {
    if (field === "status") return statusIndex.get(t.status) ?? Number.MAX_SAFE_INTEGER;
    if (field === "priority") return t.priority === 0 ? null : t.priority;
    if (field === "assignee") return t.assignee_seat_ids[0] ?? null;
    return fieldValue(t, field);
  };
  return [...tasks].sort((a, b) => {
    for (const s of sort) {
      const x = key(a, s.field);
      const y = key(b, s.field);
      if (isEmpty(x) && isEmpty(y)) continue;
      if (isEmpty(x)) return 1;
      if (isEmpty(y)) return -1;
      const c = compare(x, y);
      if (c !== 0) return s.direction === "desc" ? -c : c;
    }
    return a.number - b.number;
  });
}

export type CardGroup<T> = { key: string | null; label: string; tasks: T[] };

/** The view's groups in a stable order: statuses in board order (every status, even empty, so the
 * board shows its columns), priorities urgent → low then none, seats A–Z then "No assignee", a
 * select field's choices then "No value". A task with two assignees appears under each. */
export function groupCards<T extends CardLike>(
  tasks: readonly T[],
  groupBy: string | null,
  ctx: ViewContext,
): CardGroup<T>[] {
  if (!groupBy) return [{ key: null, label: "All tasks", tasks: [...tasks] }];
  if (groupBy === "status") {
    const groups = ctx.statuses.map((s) => ({
      key: s.key as string | null,
      label: s.name,
      tasks: tasks.filter((t) => t.status === s.key),
    }));
    const stray = tasks.filter((t) => !ctx.statuses.some((s) => s.key === t.status));
    return stray.length ? [...groups, { key: null, label: "No status", tasks: stray }] : groups;
  }
  if (groupBy === "priority") {
    return [1, 2, 3, 4, 0].map((p) => ({
      key: String(p),
      label: PRIORITY_LABELS[p] ?? String(p),
      tasks: tasks.filter((t) => t.priority === p),
    }));
  }
  if (groupBy === "assignee") {
    const label = ctx.seatLabel ?? ((id: string) => id);
    const seats = [...new Set(tasks.flatMap((t) => t.assignee_seat_ids))].sort((a, b) =>
      label(a).localeCompare(label(b)),
    );
    return [
      ...seats.map((s) => ({
        key: s as string | null,
        label: label(s),
        tasks: tasks.filter((t) => t.assignee_seat_ids.includes(s)),
      })),
      { key: null, label: "No assignee", tasks: tasks.filter((t) => !t.assignee_seat_ids.length) },
    ];
  }
  if (groupBy.startsWith("field:")) {
    const id = groupBy.slice(6);
    const field = ctx.fields.find((f) => f.id === id);
    const choices = field?.options.choices ?? [];
    const has = (t: T, k: string) => {
      const v = t.fields[id];
      return Array.isArray(v) ? v.includes(k) : v === k;
    };
    return [
      ...choices.map((c) => ({
        key: c.key as string | null,
        label: c.name,
        tasks: tasks.filter((t) => has(t, c.key)),
      })),
      { key: null, label: "No value", tasks: tasks.filter((t) => isEmpty(t.fields[id])) },
    ];
  }
  return [{ key: null, label: "All tasks", tasks: [...tasks] }];
}

/** Whether a group key is one the views can group by on this board. */
export function validGroupBy(groupBy: string | null, ctx: Pick<ViewContext, "fields">): boolean {
  if (groupBy === null) return true;
  if ((GROUP_KEYS as readonly string[]).includes(groupBy)) return true;
  if (!groupBy.startsWith("field:")) return false;
  const f = ctx.fields.find((x) => x.id === groupBy.slice(6));
  return f?.type === "select" || f?.type === "multi_select";
}

/** Filter, sort, group: what `boards/{id}/tasks?view=` answers, before paging. */
export function applyView<T extends CardLike>(
  tasks: readonly T[],
  view: Pick<View, "filter" | "sort" | "group_by">,
  ctx: ViewContext,
): CardGroup<T>[] {
  const shown = sortCards(
    tasks.filter((t) => matches(t, view.filter, ctx)),
    view.sort,
    ctx,
  );
  return groupCards(shown, view.group_by, ctx);
}

/** The values of a filter's `in` condition on a field (the toolbar's ticked choices). */
export function inValues(filter: ViewFilter, field: string): unknown[] {
  const c = (filter.all ?? []).find((x) => x.field === field && x.op === "in");
  return Array.isArray(c?.value) ? c.value : [];
}

/** The filter with that field's `in` condition replaced (dropped when no value is ticked). */
export function withIn(filter: ViewFilter, field: string, values: readonly unknown[]): ViewFilter {
  const rest = (filter.all ?? []).filter((x) => !(x.field === field && x.op === "in"));
  const all = values.length ? [...rest, { field, op: "in" as const, value: [...values] }] : rest;
  const { all: _old, ...others } = filter;
  return all.length ? { ...others, all } : others;
}

/** Whether two view settings differ (the draft bar shows while they do). */
export function sameView(
  a: Pick<View, "filter" | "sort" | "group_by">,
  b: Pick<View, "filter" | "sort" | "group_by">,
): boolean {
  const norm = (v: Pick<View, "filter" | "sort" | "group_by">) =>
    JSON.stringify({
      all: v.filter.all ?? [],
      any: v.filter.any ?? [],
      include_done: v.filter.include_done ?? true,
      sort: v.sort,
      group_by: v.group_by,
    });
  return norm(a) === norm(b);
}
